import { PageType } from "@/src/types/digitalData";

interface CategoryHierarchy {
  pageType: PageType;
  primaryCategory: string;
  subCategory1?: string;
  subCategory2?: string;
  subCategory3?: string;
}

interface RoutePattern {
  pattern: RegExp | string;
  mapper: (pathname: string, parts: string[]) => CategoryHierarchy;
}

const ROUTE_PATTERNS: RoutePattern[] = [
  {
    pattern: /^\/(404|error)/,
    mapper: () => ({ pageType: "errorpage", primaryCategory: "error" })
  },
  {
    pattern: /^\/($|moto$)/,
    mapper: () => ({ pageType: "frontpage", primaryCategory: "home" })
  },
  {
    pattern: /^\/notifications$/,
    mapper: () => ({
      pageType: "otherpage",
      primaryCategory: "notifications",
      subCategory1: "consumer"
    })
  },
  {
    pattern: /^\/dealer\/notifications$/,
    mapper: () => ({
      pageType: "otherpage",
      primaryCategory: "notifications",
      subCategory1: "dealer"
    })
  },
  {
    pattern: /^\/admin/,
    mapper: (pathname, parts) => {
      const result: CategoryHierarchy = {
        pageType: "sectionfrontpage",
        primaryCategory: "admin"
      };
      if (parts[1]) {
        result.subCategory1 = parts[1];
      }
      return result;
    }
  },
  {
    pattern: /^\/my-auctions\/(\d+)$/,
    mapper: () => ({
      pageType: "itempage",
      primaryCategory: "auction",
      subCategory1: "consumer"
    })
  },
  {
    pattern: /^\/my-auctions$/,
    mapper: () => ({
      pageType: "sectionfrontpage",
      primaryCategory: "auction",
      subCategory1: "consumer"
    })
  },
  {
    pattern: /^\/dealer\/auction\/\d+$/,
    mapper: () => ({
      pageType: "itempage",
      primaryCategory: "dealer",
      subCategory1: "auction"
    })
  },
  {
    pattern: /\/auction\/(create|edit)/,
    mapper: (pathname) => {
      const isMoto = pathname.startsWith("/moto");
      const isCreate = pathname.includes("/auction/create");
      return {
        pageType: "processpage",
        primaryCategory: "auction",
        subCategory1: isCreate ? "create" : "edit",
        subCategory2: isMoto ? "moto" : "car"
      };
    }
  },
  {
    pattern: /^\/user-settings$/,
    mapper: () => ({
      pageType: "processpage",
      primaryCategory: "settings",
      subCategory1: "consumer"
    })
  },
  {
    pattern: /^\/dealer\/user-settings$/,
    mapper: () => ({
      pageType: "processpage",
      primaryCategory: "settings",
      subCategory1: "dealer"
    })
  },
  {
    pattern: /^\/contact-info$/,
    mapper: () => ({
      pageType: "otherpage",
      primaryCategory: "contact",
      subCategory1: "consumer"
    })
  },
  {
    pattern: /^\/dealer\/contact-info$/,
    mapper: () => ({
      pageType: "otherpage",
      primaryCategory: "contact",
      subCategory1: "dealer"
    })
  },
  {
    pattern: /^\/blog$/,
    mapper: () => ({ pageType: "sectionfrontpage", primaryCategory: "blog" })
  },
  {
    pattern: /^\/blog\//,
    mapper: () => ({ pageType: "blogpost", primaryCategory: "blog" })
  },
  {
    pattern: /^\/ostetaan-autoja$/,
    mapper: () => ({ pageType: "searchpage", primaryCategory: "search" })
  },
  {
    pattern: /\/ostetaan-autoja/,
    mapper: () => ({ pageType: "searchresultpage", primaryCategory: "search" })
  },
  {
    pattern: /^\/dealer/,
    mapper: (pathname, parts) => {
      const subpage = parts[1];
      if (!subpage) {
        return { pageType: "sectionfrontpage", primaryCategory: "dealer" };
      }
      const auctionSubpages = [
        "all-active",
        "my",
        "accepted",
        "negotiations",
        "pending",
        "completed",
        "declined",
        "closed",
        "all-ended"
      ];
      if (auctionSubpages.includes(subpage)) {
        return {
          pageType: "sectionfrontpage",
          primaryCategory: "dealer",
          subCategory1: "auctions",
          subCategory2: subpage
        };
      }
      if (subpage === "instructions") {
        return {
          pageType: "otherpage",
          primaryCategory: "dealer",
          subCategory1: "instructions"
        };
      }
      return {
        pageType: "sectionfrontpage",
        primaryCategory: "dealer",
        subCategory1: subpage
      };
    }
  },
  {
    pattern: /^\/faq$/,
    mapper: () => ({ pageType: "sectionfrontpage", primaryCategory: "faq" })
  }
];

/**
 * Maps route pathname to pageType, primaryCategory, and optional subcategories
 * Returns category hierarchy based on URL structure and site taxonomy
 */
export const getPageTypeAndCategory = (pathname: string): CategoryHierarchy => {
  const parts = pathname.split("/").filter(Boolean);

  for (const { pattern, mapper } of ROUTE_PATTERNS) {
    if (
      typeof pattern === "string"
        ? pathname === pattern
        : pattern.test(pathname)
    ) {
      return mapper(pathname, parts);
    }
  }

  // Fallback for other pages
  return { pageType: "otherpage", primaryCategory: "general" };
};
